import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { FaArrowLeft, FaUser, FaCalendar, FaBook, FaPlay, FaCamera, FaArrowRight } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import { getStoryById } from '../data/exampleStories';
import BookReader from '../components/BookReader';
import { applyImageFallback, getStoryCoverFallback } from '../config/imageFallbacks';
import '../styles/story-page.css';

const StoryPage = () => {
  const { id } = useParams();
  const story = getStoryById(parseInt(id));
  const [selectedStory, setSelectedStory] = useState(null);
  const [showPDFViewer, setShowPDFViewer] = useState(false);
  const { t } = useTranslation();

  const localizedStory = story
    ? {
        ...story,
        title: t(`stories.${story.id}.title`, { defaultValue: story.title }),
        subtitle: t(`stories.${story.id}.subtitle`, { defaultValue: story.subtitle }),
        need: t(`stories.${story.id}.need`, { defaultValue: story.need }),
        description: t(`stories.${story.id}.description`, { defaultValue: story.description })
      }
    : null;

  const storyWorldLabel = story ? t(`common.themes.${story.theme}`, { defaultValue: story.theme }) : '';
  const styleLabel = story ? t(`common.styles.${story.style}`, { defaultValue: story.style }) : '';

  const handleReadStory = () => {
    setSelectedStory(story);
    setShowPDFViewer(true);
  };

  const closePDFViewer = () => {
    setShowPDFViewer(false);
    setSelectedStory(null);
  };

  const renderMultiline = (text) => text.split('\n').map((line, idx, arr) => (
    <React.Fragment key={`${line}-${idx}`}>
      {line}
      {idx < arr.length - 1 && <br />}
    </React.Fragment>
  ));

  if (!story) {
    return (
      <div className="story-page-error">
        <div className="container">
          <div className="error-content">
            <h1>{t('storyPage.notFoundTitle')}</h1>
            <p>{t('storyPage.notFoundMessage')}</p>
            <Link to="/examples" className="back-link">
              <FaArrowLeft />
              {t('storyPage.backToExamples')}
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const pageCount = story.storyPreview?.pageCount || 24;

  const pdfUrl = story.pdfUrl || '/pdfs/alex_compressed.pdf';

  return (
    <div className="story-page">
      <div className="container">
        {/* Navigation */}
        <div className="story-nav">
          <Link to="/examples" className="back-link">
            <FaArrowLeft />
            <span>{t('storyPage.backToExamples')}</span>
          </Link>
        </div>

        {/* Story Showcase Header */}
        <div className="story-showcase">
          <div className="story-showcase-content">
            <div className="story-cover-section">
              <div className="story-cover-wrapper">
                <img 
                  src={story.coverImage} 
                  alt={localizedStory.title}
                  className="story-cover-image"
                  onError={(e) => applyImageFallback(e, getStoryCoverFallback(story.id))}
                />
              </div>
              
              {/* Child Photo */}
              <div className="child-photo-placeholder">
                {story.childPhoto ? (
                  <div className="child-photo-display">
                    <div className="photo-oval">
                      <img 
                        src={story.childPhoto} 
                        alt={`${localizedStory.title} main character`}
                        className="child-photo-img"
                        onError={(e) => { 
                          e.target.style.display = 'none';
                          e.target.nextElementSibling.style.display = 'flex';
                        }}
                      />
                      <div className="photo-fallback" style={{ display: 'none' }}>
                        <FaCamera className="camera-icon" />
                        <span>{renderMultiline(t('storyPage.photoUnavailable'))}</span>
                      </div>
                    </div>
                    <p className="photo-note">
                      {t('storyPage.photoNote')}
                    </p>
                  </div>
                ) : (
                  <div className="photo-oval">
                    <FaCamera className="camera-icon" />
                    <span>{renderMultiline(t('storyPage.addPhoto'))}</span>
                  </div>
                )}
              </div>
            </div>

            <div className="story-info-section">
              <div className="story-header">
                <h1 className="story-title">{localizedStory.title}</h1>
                <p className="story-subtitle">{localizedStory.subtitle}</p>
                
                <div className="story-meta-badges">
                  <div className="meta-badge">
                    <FaUser />
                    <span>{t('storyPage.byAuthor', { author: story.author })}</span>
                  </div>
                  <div className="meta-badge">
                    <FaCalendar />
                    <span>{t('storyPage.ageBadge', { age: story.age })}</span>
                  </div>
                  <div className="meta-badge">
                    <FaBook />
                    <span>{t('storyPage.pageCountLabel', { count: pageCount })}</span>
                  </div>
                </div>

                <div className="story-tags">
                  <span className="story-need-tag">
                    {localizedStory.need}
                  </span>
                  <span className="story-world-tag">
                    {storyWorldLabel}
                  </span>
                  <span className="story-style-tag">{styleLabel}</span>
                </div>
              </div>

              <div className="story-description">
                <h3>{t('storyPage.storySummary')}</h3>
                <p>{localizedStory.description}</p>
              </div>

              <div className="story-action-section">
                <button 
                  onClick={handleReadStory}
                  className="read-story-main-btn"
                >
                  <FaPlay className="play-icon" />
                  {t('storyPage.readThisStory')}
                </button>
                <p className="story-action-note">{t('storyPage.readingExperience')}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Story Details Section */}
        <div className="story-details-section">
          <div className="details-grid">
            <div className="detail-card">
              <h4>{t('storyPage.storyInformation')}</h4>
              <div className="detail-items">
                <div className="detail-item">
                  <strong>{t('storyPage.need')}</strong>
                  <span>{localizedStory.need}</span>
                </div>
                <div className="detail-item">
                  <strong>{t('storyPage.storyWorld')}</strong>
                  <span>{storyWorldLabel}</span>
                </div>
                <div className="detail-item">
                  <strong>{t('storyPage.ageRange')}</strong>
                  <span>{t('storyPage.ageYears', { age: story.age })}</span>
                </div>
                <div className="detail-item">
                  <strong>{t('storyPage.style')}</strong>
                  <span>{styleLabel}</span>
                </div>
                <div className="detail-item">
                  <strong>{t('storyPage.characters')}</strong>
                  <span>{t('storyPage.charactersCount', { count: story.characters })}</span>
                </div>
              </div>
            </div>

            <div className="detail-card">
              <h4>{t('storyPage.previewDetails')}</h4>
              <div className="detail-items">
                <div className="detail-item">
                  <strong>{t('storyPage.pages')}</strong>
                  <span>{pageCount}</span>
                </div>
                <div className="detail-item">
                  <strong>{t('storyPage.readingTime')}</strong>
                  <span>{t('storyPage.minutes', { minutes: Math.ceil(pageCount * 1.5) })}</span>
                </div>
                <div className="detail-item">
                  <strong>{t('storyPage.photoPersonalization')}</strong>
                  <span>{story.childPhoto ? t('storyPage.photoIncluded') : t('storyPage.photoOptional')}</span>
                </div>
                <div className="detail-item">
                  <strong>{t('storyPage.delivery')}</strong>
                  <span>{t('storyPage.digitalOrPrint')}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Call to Action Section */}
        <div className="story-cta-section">
          <div className="cta-content">
            <h2>{t('storyPage.ctaTitle')}</h2>
            <p>
              {t('storyPage.ctaSubtitle')}
            </p>
            <div className="cta-buttons">
              <Link to="/create-story" className="cta-button primary">
                {t('storyPage.ctaPrimary')}
                <FaArrowRight />
              </Link>
              <Link to="/examples" className="cta-button secondary">
                {t('storyPage.ctaSecondary')}
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* PDF Viewer Modal */}
      {selectedStory && showPDFViewer && (
        <BookReader
          pdfUrl={pdfUrl}
          onClose={closePDFViewer}
        />
      )}
    </div>
  );
};

export default StoryPage;
